package com.bikompanion.ui.composables

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.tween
import androidx.compose.animation.expandVertically
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.shrinkVertically
import androidx.compose.animation.togetherWith
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Directions
import androidx.compose.material.icons.filled.Share
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.ButtonGroupDefaults
import androidx.compose.material3.FilledTonalButton
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import dev.chrisbanes.haze.HazeState
import com.bikompanion.domain.BicingNetworkId
import com.bikompanion.domain.BikeNetwork
import com.bikompanion.domain.BikeStation
import com.bikompanion.domain.BikeType
import com.bikompanion.domain.StationWatch
import com.bikompanion.ui.preview.GlassPreviewSurface
import com.bikompanion.ui.preview.PreviewEmptyStation
import com.bikompanion.ui.preview.PreviewLightDarkCompact
import com.bikompanion.ui.preview.PreviewLightDarkExpanded
import com.bikompanion.ui.preview.PreviewNetwork
import com.bikompanion.ui.preview.PreviewStation
import com.bikompanion.ui.preview.PreviewSurface
import com.bikompanion.ui.strings.BikompanionStrings
import com.bikompanion.ui.strings.LocalStrings

@Composable
internal fun StationDetailsSheet(
    station: BikeStation?,
    network: BikeNetwork?,
    hazeState: HazeState,
    onDismiss: () -> Unit,
    onShareClick: () -> Unit,
    watches: List<StationWatch>,
    onWatch: (BikeStation, BikeType) -> Unit,
    onCancelWatch: (BikeStation, BikeType) -> Unit,
    docked: Boolean = false,
    dockedWidth: Dp = DockedSheetWidth,
    watchRequiresInstall: Boolean = false,
) {
    // The station goes null the instant the sheet is dismissed, so the last-known values are kept
    // around long enough for GlassBottomSheet's exit transition to still have something to draw.
    var lastStation by remember { mutableStateOf(station) }
    var lastNetwork by remember { mutableStateOf(network) }
    if (station != null) {
        lastStation = station
        lastNetwork = network
    }
    // Keyed on the station so that tapping another marker doesn't carry an open "Details" over.
    var detailsExpanded by remember(lastStation?.id) { mutableStateOf(false) }

    val operator = lastStation?.let { operatorAppFor(it.networkId, lastNetwork?.name) }
    val openOperatorApp = rememberOperatorAppLauncher(operator)
    val ambiciHref = lastStation
        ?.takeIf { isAmbici(it.networkId, lastNetwork?.name) }
        ?.let { lastNetwork?.href?.takeIf { href -> href.isNotEmpty() } }

    GlassBottomSheet(
        visible = station != null,
        hazeState = hazeState,
        onDismiss = onDismiss,
        docked = docked,
        dockedWidth = dockedWidth,
        // Pinned outside the scroll so the one action people open this sheet for is never
        // buried under the details below it.
        footer = if (operator != null && openOperatorApp != null) {
            {
                OperatorActions(
                    operatorName = operator.name,
                    ambiciHref = ambiciHref,
                    onOpenOperatorApp = openOperatorApp,
                )
            }
        } else {
            null
        },
    ) {
        val currentStation = lastStation ?: return@GlassBottomSheet
        val openDirections = rememberDirectionsLauncher(
            latitude = currentStation.latitude,
            longitude = currentStation.longitude,
            label = currentStation.name,
        )
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(bottom = 8.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            AnimatedText(
                text = currentStation.name,
                style = MaterialTheme.typography.titleLarge,
                modifier = Modifier.weight(1f),
            )
            IconButton(onClick = onShareClick) {
                Icon(
                    imageVector = Icons.Default.Share,
                    contentDescription = LocalStrings.current.actionShareStation,
                )
            }
            IconButton(
                onClick = openDirections,
            ) {
                Icon(
                    imageVector = Icons.Default.Directions,
                    contentDescription = LocalStrings.current.actionGetDirections,
                )
            }
        }
        StationAvailabilityBar(
            station = currentStation,
            modifier = Modifier.fillMaxWidth(),
        )

        // One child so the buttons coming and going don't each add a gap from the sheet's spacing;
        // each button carries its own top padding inside its animation instead.
        Column {
            listOf(BikeType.Normal, BikeType.Electric).forEach { bikeType ->
                val unavailable = when (bikeType) {
                    BikeType.Normal -> currentStation.normalBikes == 0
                    BikeType.Electric -> currentStation.electricBikes == 0
                }
                val existing = watches.any {
                    it.networkId == currentStation.networkId &&
                        it.stationId == currentStation.id &&
                        it.bikeType == bikeType
                }
                // A running watch stays cancellable: only starting one needs the installed app.
                val installRequired = watchRequiresInstall && !existing
                WatchButton(
                    visible = unavailable && (bikeType == BikeType.Normal || currentStation.supportsElectricBikes),
                    label = if (installRequired) {
                        bikeType.installToWatchLabel(LocalStrings.current)
                    } else {
                        bikeType.watchLabel(LocalStrings.current, existing)
                    },
                    enabled = !installRequired,
                    onClick = {
                        if (existing) onCancelWatch(currentStation, bikeType) else onWatch(currentStation, bikeType)
                    },
                )
            }
        }

        ExpandableDetails(
            expanded = detailsExpanded,
            onToggle = { detailsExpanded = !detailsExpanded },
        ) {
            StationDetails(station = currentStation, network = lastNetwork)
        }
    }
}

/** Grows in and out as stations that need it (or don't) swap into the sheet, rather than popping. */
@Composable
private fun WatchButton(
    visible: Boolean,
    label: String,
    enabled: Boolean,
    onClick: () -> Unit,
) {
    AnimatedVisibility(
        visible = visible,
        enter = expandVertically() + fadeIn(),
        exit = shrinkVertically() + fadeOut(),
    ) {
        FilledTonalButton(
            onClick = onClick,
            enabled = enabled,
            modifier = Modifier
                .fillMaxWidth()
                .padding(top = 4.dp),
        ) {
            AnimatedText(label)
        }
    }
}

/** Everything about the station and its network that is true but rarely what a rider came for. */
@Composable
private fun StationDetails(
    station: BikeStation,
    network: BikeNetwork?,
) {
    StationField(LocalStrings.current.fieldCapacity, station.capacity.toString())
    StationField(LocalStrings.current.fieldStationId, station.id)
    StationField(LocalStrings.current.fieldCoordinates, "${station.latitude}, ${station.longitude}")
    // The networks list may not have reached us yet — the station's own fields above are always
    // known, the network's only once it has.
    if (network != null) {
        Text(
            text = LocalStrings.current.fieldNetwork,
            style = MaterialTheme.typography.titleSmall,
            modifier = Modifier.padding(top = 12.dp, bottom = 4.dp),
        )
        StationField(LocalStrings.current.fieldName, network.name)
        StationField(LocalStrings.current.fieldCity, network.city)
        StationField(LocalStrings.current.fieldCountry, network.country)
        if (network.company.isNotEmpty()) {
            StationField(LocalStrings.current.fieldCompany, network.company.joinToString(", "))
        }
        if (network.licenseName.isNotEmpty()) {
            StationField(LocalStrings.current.fieldLicense, network.licenseName)
        }
        if (network.licenseUrl.isNotEmpty()) {
            StationField(LocalStrings.current.fieldLicenseUrl, network.licenseUrl)
        }
    }
}

/**
 * The full-width operator action. AMBici networks get two routes side by side — AMBici's own site
 * and the Bicing app that also serves them; everything else gets the single operator button.
 */
@Composable
private fun OperatorActions(
    operatorName: String,
    ambiciHref: String?,
    onOpenOperatorApp: () -> Unit,
) {
    val uriHandler = LocalUriHandler.current
    // Keyed on the href so moving between a one-button and a two-button network cross-fades the
    // layout (and its size) instead of snapping.
    AnimatedContent(
        targetState = ambiciHref,
        transitionSpec = { fadeIn(tween(durationMillis = 220)) togetherWith fadeOut(tween(durationMillis = 120)) },
        label = "OperatorActions",
    ) { href ->
        if (href != null) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(ButtonGroupDefaults.ConnectedSpaceBetween),
            ) {
                Button(
                    onClick = { uriHandler.openUri(href) },
                    shapes = ButtonDefaults.shapes(
                        shape = ButtonGroupDefaults.connectedLeadingButtonShape,
                        pressedShape = ButtonGroupDefaults.connectedLeadingButtonPressShape,
                    ),
                    modifier = Modifier.weight(1f),
                ) {
                    Text(LocalStrings.current.actionOpenOperator("Ambici"))
                }
                Button(
                    onClick = onOpenOperatorApp,
                    shapes = ButtonDefaults.shapes(
                        shape = ButtonGroupDefaults.connectedTrailingButtonShape,
                        pressedShape = ButtonGroupDefaults.connectedTrailingButtonPressShape,
                    ),
                    modifier = Modifier.weight(1f),
                ) {
                    AnimatedText(LocalStrings.current.actionOpenOperator(operatorName))
                }
            }
        } else {
            Button(
                onClick = onOpenOperatorApp,
                modifier = Modifier.fillMaxWidth(),
            ) {
                AnimatedText(LocalStrings.current.actionOpenOperator(operatorName))
            }
        }
    }
}

private fun BikeType.watchLabel(
    strings: BikompanionStrings,
    watching: Boolean,
) = when (this) {
    BikeType.Normal -> if (watching) strings.watchStopNormal else strings.watchNormal
    BikeType.Electric -> if (watching) strings.watchStopElectric else strings.watchElectric
}

private fun BikeType.installToWatchLabel(strings: BikompanionStrings) =
    when (this) {
        BikeType.Normal -> strings.watchInstallNormal
        BikeType.Electric -> strings.watchInstallElectric
    }

@PreviewLightDarkCompact
@Composable
private fun StationDetailsSheetBottomPreview() {
    StationDetailsSheetPreview(station = PreviewStation)
}

@PreviewLightDarkExpanded
@Composable
private fun StationDetailsSheetDockedPreview() {
    StationDetailsSheetPreview(station = PreviewStation, docked = true)
}

@PreviewLightDarkCompact
@Composable
private fun StationDetailsSheetOperatorPreview() {
    StationDetailsSheetPreview(station = PreviewStation.copy(networkId = BicingNetworkId))
}

/** Out of both kinds of bike, so the sheet offers to watch for each. */
@PreviewLightDarkCompact
@Composable
private fun StationDetailsSheetNoBikesPreview() {
    StationDetailsSheetPreview(station = PreviewEmptyStation)
}

/** Out of bikes where e-bikes never come, so there is only the one kind to watch for. */
@PreviewLightDarkCompact
@Composable
private fun StationDetailsSheetNoElectricSupportPreview() {
    StationDetailsSheetPreview(station = PreviewEmptyStation.copy(supportsElectricBikes = false))
}

@PreviewLightDarkCompact
@Composable
private fun StationDetailsSheetWatchingPreview() {
    StationDetailsSheetPreview(
        station = PreviewEmptyStation,
        watches = BikeType.entries.map { bikeType ->
            StationWatch(
                networkId = PreviewEmptyStation.networkId,
                stationId = PreviewEmptyStation.id,
                stationName = PreviewEmptyStation.name,
                bikeType = bikeType,
                expiresAtEpochMillis = 0L,
            )
        },
    )
}

@PreviewLightDarkCompact
@Composable
private fun StationDetailsSheetWatchRequiresInstallPreview() {
    StationDetailsSheetPreview(station = PreviewEmptyStation, watchRequiresInstall = true)
}

// The sheet opens with its details collapsed and only offers the footer where the platform can
// launch the operator's app, so these two are shown on their own to be sure of seeing them.
@Preview
@Composable
private fun StationDetailsPreview() {
    PreviewSurface {
        Column(modifier = Modifier.padding(16.dp)) {
            StationDetails(station = PreviewStation, network = PreviewNetwork)
        }
    }
}

/** The networks list not having arrived yet, so only the station's own fields are known. */
@Preview
@Composable
private fun StationDetailsNoNetworkPreview() {
    PreviewSurface {
        Column(modifier = Modifier.padding(16.dp)) {
            StationDetails(station = PreviewStation, network = null)
        }
    }
}

@Preview
@Composable
private fun OperatorActionsPreview() {
    PreviewSurface {
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp),
        ) {
            OperatorActions(operatorName = "Bicing", ambiciHref = null, onOpenOperatorApp = {})
            OperatorActions(operatorName = "Bicing", ambiciHref = "https://ambici.example", onOpenOperatorApp = {})
        }
    }
}

@Composable
private fun StationDetailsSheetPreview(
    station: BikeStation,
    docked: Boolean = false,
    watches: List<StationWatch> = emptyList(),
    watchRequiresInstall: Boolean = false,
) {
    GlassPreviewSurface { hazeState ->
        StationDetailsSheet(
            station = station,
            network = PreviewNetwork,
            hazeState = hazeState,
            onDismiss = {},
            onShareClick = {},
            watches = watches,
            onWatch = { _, _ -> },
            onCancelWatch = { _, _ -> },
            docked = docked,
            watchRequiresInstall = watchRequiresInstall,
        )
    }
}
